/**
 * First-touch + last-touch UTM / click-id capture for SiteGlue marketing → app flows.
 * Persists to cookie + sessionStorage so cross-page and siteglue.ai → app.easygpt.builders
 * sign-up keeps attribution for GA4 and future CRM sync.
 */

export const MARKETING_ATTRIBUTION_FIRST_COOKIE = "sg_attr_first";
export const MARKETING_ATTRIBUTION_LAST_COOKIE = "sg_attr_last";
export const MARKETING_ATTRIBUTION_SESSION_FIRST = "sg_attr_first";
export const MARKETING_ATTRIBUTION_SESSION_LAST = "sg_attr_last";

/** Days to retain attribution cookies (align with typical ad attribution windows). */
export const MARKETING_ATTRIBUTION_COOKIE_MAX_AGE_SEC = 60 * 60 * 24 * 90;

export const MARKETING_ATTRIBUTION_PARAM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "gclid",
  "fbclid",
  "msclkid",
  /** X (Twitter) click id — required for Click ID Tracking / better CAPI match. */
  "twclid",
] as const;

export type MarketingAttributionParamKey = (typeof MARKETING_ATTRIBUTION_PARAM_KEYS)[number];

export type MarketingAttributionTouch = Partial<
  Record<MarketingAttributionParamKey, string>
> & {
  /** ISO timestamp when this touch was captured */
  captured_at?: string;
  /** Path where the touch was captured */
  landing_path?: string;
};

export type MarketingAttributionSnapshot = {
  first: MarketingAttributionTouch | null;
  last: MarketingAttributionTouch | null;
};

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function readCookie(name: string): string | null {
  if (!isBrowser()) return null;
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match?.[1] ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string): void {
  if (!isBrowser()) return;
  document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${MARKETING_ATTRIBUTION_COOKIE_MAX_AGE_SEC}; SameSite=Lax`;
}

function parseTouchJson(raw: string | null): MarketingAttributionTouch | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as MarketingAttributionTouch;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function persistTouch(key: "first" | "last", touch: MarketingAttributionTouch): void {
  if (!isBrowser()) return;
  const serialized = JSON.stringify(touch);
  const cookieKey =
    key === "first" ? MARKETING_ATTRIBUTION_FIRST_COOKIE : MARKETING_ATTRIBUTION_LAST_COOKIE;
  const sessionKey =
    key === "first" ? MARKETING_ATTRIBUTION_SESSION_FIRST : MARKETING_ATTRIBUTION_SESSION_LAST;
  writeCookie(cookieKey, serialized);
  try {
    sessionStorage.setItem(sessionKey, serialized);
  } catch {
    /* ignore */
  }
}

/** Extract attribution fields from URL search params. */
export function parseAttributionFromSearchParams(
  searchParams: URLSearchParams,
): MarketingAttributionTouch | null {
  const touch: MarketingAttributionTouch = {};
  let hasAny = false;
  for (const key of MARKETING_ATTRIBUTION_PARAM_KEYS) {
    const value = searchParams.get(key)?.trim();
    if (value) {
      touch[key] = value;
      hasAny = true;
    }
  }
  return hasAny ? touch : null;
}

/** Parse from full query string or window.location.search. */
export function parseAttributionFromQuery(search?: string): MarketingAttributionTouch | null {
  if (!search) return null;
  const normalized = search.startsWith("?") ? search : `?${search}`;
  return parseAttributionFromSearchParams(new URLSearchParams(normalized));
}

/**
 * Capture UTMs from the current URL (or provided search).
 * First touch is set once; last touch updates on every tagged landing.
 */
export function captureMarketingAttribution(search?: string): MarketingAttributionSnapshot {
  if (!isBrowser()) {
    return { first: null, last: null };
  }

  const query =
    search ??
    `${window.location.search}${window.location.hash.includes("?") ? window.location.hash.split("?")[1] ?? "" : ""}`;
  const incoming = parseAttributionFromQuery(
    query.startsWith("?") ? query : query ? `?${query}` : window.location.search,
  );

  const existingFirst =
    parseTouchJson(sessionStorage.getItem(MARKETING_ATTRIBUTION_SESSION_FIRST)) ??
    parseTouchJson(readCookie(MARKETING_ATTRIBUTION_FIRST_COOKIE));
  const existingLast =
    parseTouchJson(sessionStorage.getItem(MARKETING_ATTRIBUTION_SESSION_LAST)) ??
    parseTouchJson(readCookie(MARKETING_ATTRIBUTION_LAST_COOKIE));

  if (!incoming) {
    return { first: existingFirst, last: existingLast };
  }

  const enriched: MarketingAttributionTouch = {
    ...incoming,
    captured_at: new Date().toISOString(),
    landing_path: window.location.pathname,
  };

  if (!existingFirst) {
    persistTouch("first", enriched);
  }
  persistTouch("last", enriched);

  return {
    first: existingFirst ?? enriched,
    last: enriched,
  };
}

export function readMarketingAttribution(): MarketingAttributionSnapshot {
  if (!isBrowser()) {
    return { first: null, last: null };
  }
  const first =
    parseTouchJson(sessionStorage.getItem(MARKETING_ATTRIBUTION_SESSION_FIRST)) ??
    parseTouchJson(readCookie(MARKETING_ATTRIBUTION_FIRST_COOKIE));
  const last =
    parseTouchJson(sessionStorage.getItem(MARKETING_ATTRIBUTION_SESSION_LAST)) ??
    parseTouchJson(readCookie(MARKETING_ATTRIBUTION_LAST_COOKIE));
  return { first, last };
}

/** Flatten last-touch (fallback first) for GA4 / GTM event params. */
export function getAttributionAnalyticsPayload(): Record<string, string> {
  const { first, last } = readMarketingAttribution();
  const touch = last ?? first;
  if (!touch) return {};

  const payload: Record<string, string> = {};
  for (const key of MARKETING_ATTRIBUTION_PARAM_KEYS) {
    const value = touch[key];
    if (value) payload[key] = value;
  }
  if (touch.landing_path) payload.landing_path = touch.landing_path;
  return payload;
}

/** Campaign stamped onto Get Started links from these public pages. */
const CAMPAIGN_BY_PATH: Record<string, string> = {
  "/agent-ready": "agent_ready",
  "/ai-receptionist": "ai_receptionist",
  "/ai-phone-system": "ai_phone_system",
  "/scheduling-specialist": "ai_scheduling",
  "/online-support-agent": "ai_customer_support",
};

/** Append stored attribution params to signup / hire URLs. */
export function appendAttributionToUrl(href: string): string {
  if (!isBrowser()) return href;
  const { last, first } = readMarketingAttribution();
  const touch = last ?? first;

  try {
    const base = href.startsWith("http") ? undefined : window.location.origin;
    const url = new URL(href, base ?? "https://www.siteglue.ai");
    if (touch) {
      for (const key of MARKETING_ATTRIBUTION_PARAM_KEYS) {
        const value = touch[key];
        if (value && !url.searchParams.has(key)) {
          url.searchParams.set(key, value);
        }
      }
    }
    const pageCampaign = CAMPAIGN_BY_PATH[window.location.pathname];
    if (pageCampaign && !url.searchParams.has("utm_campaign")) {
      url.searchParams.set("utm_campaign", pageCampaign);
    }
    if (href.startsWith("/")) {
      return `${url.pathname}${url.search}${url.hash}`;
    }
    return url.href;
  } catch {
    return href;
  }
}

export function appendAttributionToParams(
  params: Record<string, string | undefined>,
): Record<string, string | undefined> {
  const analytics = getAttributionAnalyticsPayload();
  return {
    ...params,
    ...Object.fromEntries(
      MARKETING_ATTRIBUTION_PARAM_KEYS.map((key) => [key, analytics[key]]).filter(
        ([, v]) => typeof v === "string" && v.length > 0,
      ),
    ),
  };
}

/** Build final URL with UTMs for Google Ads destination templates. */
export function buildPaidLandingUrl(
  path: string,
  utm: {
    source: string;
    medium: string;
    campaign: string;
    content?: string;
    term?: string;
  },
): string {
  const normalized = path.startsWith("/") ? path : `/${path}`;
  const params = new URLSearchParams({
    utm_source: utm.source,
    utm_medium: utm.medium,
    utm_campaign: utm.campaign,
  });
  if (utm.content) params.set("utm_content", utm.content);
  if (utm.term) params.set("utm_term", utm.term);
  return `${normalized}?${params.toString()}`;
}
